// ─────────────────────────────────────────────────────────────
// MarketSentimentWidget
//
// Reads the latest AI-generated market sentiment summary from the
// `market_sentiment` table (single row, id=1). The sentiment-analyzer
// Edge Function refreshes this row every 30 minutes via pg_cron.
//
// Compliance:
//   - Non-dismissible disclaimer
//   - Source citations shown as clickable links
//   - No buy/sell/recommendation language anywhere
// ─────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { C } from "../../lib/colors";
import { getContextualTimestampLabel, getMarketState } from "../../lib/marketHours";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";

const REFRESH_MS = 5 * 60 * 1000; // refetch the cached row every 5 min
const TICKER_MS = 30 * 1000;      // refresh local time context arrays every 30 seconds

const RISK_STATE_DESCRIPTIONS = {
  "Risk-Off":
    "Fear is in the driver's seat. Capital is rotating from stocks into safe havens. Volatility tends to expand and reversals are common.",
  "Risk-On":
    "Confidence is in the driver's seat. Capital is chasing returns. Trends often extend and pullbacks frequently find buyers.",
  "Neutral":
    "Mixed signals across asset classes. No strong directional flow. Sessions often resolve into range-bound or rotational activity.",
};

export function MarketSentimentWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Local time state driver to handle instant runtime calculations
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    let cancelled = false;

    const fetchSentiment = async () => {
      try {
        const res = await authFetch(
          `${SUPABASE_URL}/rest/v1/market_sentiment?id=eq.1&select=*`
        );
        if (!res.ok) throw new Error(`Supabase returned ${res.status}`);
        const rows = await res.json();
        if (cancelled) return;
        setData(rows?.[0] || null);
        setLoading(false);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error("[MarketSentimentWidget] fetch failed:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchSentiment();
    
    // Set up standard intervals
    const fetchInterval = setInterval(fetchSentiment, REFRESH_MS);
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, TICKER_MS);

    return () => {
      cancelled = true;
      clearInterval(fetchInterval);
      clearInterval(clockInterval);
    };
  }, []);

  // Compute live market hours profiling
  const marketStateInfo = getMarketState(currentTime);
  const dynamicLabel = getContextualTimestampLabel(data?.updated_at, marketStateInfo);

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <SectionTitle>Market Sentiment</SectionTitle>
        <div style={{ fontSize: 10, color: C.muted, fontWeight: 500 }}>
          {dynamicLabel}
        </div>
      </div>

      {loading ? (
        <div style={{ color: C.muted, fontSize: 12 }}>Loading…</div>
      ) : error ? (
        <div style={{ color: C.muted, fontSize: 12 }}>⚠️ Could not load sentiment</div>
      ) : data ? (
        <>
          {/* Top row: sentiment label + impact badge */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <SentimentBadge sentiment={data.sentiment} />
            <ImpactBadge impact={data.impact_level} />
          </div>

          {/* What this state means — descriptive only, not prescriptive */}
          {RISK_STATE_DESCRIPTIONS[data.sentiment] && (
            <div style={{
              fontSize: 12,
              lineHeight: 1.55,
              color: C.sub,
              fontStyle: "italic",
              marginBottom: 14,
              paddingLeft: 10,
              borderLeft: `2px solid ${C.border}`,
            }}>
              {RISK_STATE_DESCRIPTIONS[data.sentiment]}
            </div>
          )}

          {/* Summary paragraph */}
          <div style={{
            fontSize: 13,
            lineHeight: 1.55,
            color: C.text,
            marginBottom: 14,
          }}>
            {data.summary || "No summary available."}
          </div>

          {/* Sources */}
          {Array.isArray(data.sources) && data.sources.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                Sources
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {data.sources.slice(0, 6).map((s, i) => (
                  <a
                    key={i}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: 11,
                      padding: "4px 10px",
                      background: C.blue + "15",
                      border: `1px solid ${C.blue}40`,
                      borderRadius: 6,
                      color: C.blue,
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 200,
                    }}
                    title={s.title || s.url}
                  >
                    {(s.title || s.url || "source").slice(0, 30)}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Disclaimer — always visible, non-dismissible */}
          <div style={{
            fontSize: 10,
            color: C.muted,
            padding: "8px 10px",
            background: "#1a1d2e",
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            lineHeight: 1.5,
          }}>
            <strong style={{ color: C.yellow }}>⚠ AI-generated.</strong> For educational purposes only. Not financial advice. May contain errors — verify with the source links above. Trading futures involves substantial risk of loss.
          </div>
        </>
      ) : (
        <div style={{ color: C.muted, fontSize: 12 }}>No sentiment data yet.</div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────
function SentimentBadge({ sentiment }) {
  const map = {
    "Risk-Off": { color: C.red,    bg: C.red    + "20", icon: "▼" },
    "Risk-On":  { color: C.green,  bg: C.green  + "20", icon: "▲" },
    "Neutral":  { color: C.muted,  bg: C.muted  + "20", icon: "●" },
  };
  const s = map[sentiment] || map["Neutral"];

  return (
    <div style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 12px",
      background: s.bg,
      border: `1px solid ${s.color}50`,
      borderRadius: 6,
      fontSize: 13,
      fontWeight: 700,
      color: s.color,
      letterSpacing: "0.02em",
    }}>
      <span>{s.icon}</span>
      <span>{sentiment || "Neutral"}</span>
    </div>
  );
}

function ImpactBadge({ impact }) {
  const map = {
    "High":   { color: C.red,    label: "High Impact"   },
    "Medium": { color: C.yellow, label: "Medium Impact" },
    "Low":    { color: C.muted,  label: "Low Impact"    },
  };
  const i = map[impact] || map["Low"];

  return (
    <div style={{
      fontSize: 11,
      padding: "4px 10px",
      border: `1px solid ${i.color}50`,
      borderRadius: 6,
      color: i.color,
      fontWeight: 600,
      letterSpacing: "0.02em",
    }}>
      {i.label}
    </div>
  );
}